import React, { useState } from 'react';
import { ButtonBase, type SxProps, type Theme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import RoundedMenu, { RoundedMenuItem } from 'components/atomic/molecules/rounded-menu';

export interface PillSelectOption<T extends string | number> {
  value: T;
  /** Label shown in the closed control. */
  label: React.ReactNode;
  /** Label shown in the dropdown list (defaults to `label`). */
  menuLabel?: React.ReactNode;
}

export interface PillSelectProps<T extends string | number> {
  value: T;
  onChange: (value: T) => void;
  options: PillSelectOption<T>[];
  /** Icon rendered before the label in the closed control. */
  leadingIcon?: React.ReactNode;
  ariaLabel?: string;
  /** Control height (any CSS length). Defaults to '1.5rem'. */
  height?: number | string;
  /** Font size of the closed control. Defaults to '0.625rem'. */
  fontSize?: number | string;
  /** Minimum width of the dropdown menu. Defaults to 200. */
  menuMinWidth?: number;
  /** sx merged onto the pill trigger (border/colour overrides). */
  sx?: SxProps<Theme>;
}

/**
 * PillSelect
 *
 * Atomic level: Molecule.
 *
 * A pill-shaped menu-button: an optional leading icon, the selected label, and a
 * chevron that rotates when open — dropping a `RoundedMenu` whose items show a
 * pill hover highlight and a checkmark on the selected row. Zero domain
 * knowledge; options carry an optional separate `menuLabel` for the list.
 */
export default function PillSelect<T extends string | number>({
  value,
  onChange,
  options,
  leadingIcon,
  ariaLabel,
  height = '1.5rem',
  fontSize = '0.625rem',
  menuMinWidth = 200,
  sx,
}: PillSelectProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const selected = options.find(option => option.value === value);
  const controlHeight = typeof height === 'number' ? `${height}px` : height;

  const handleSelect = (next: T) => {
    onChange(next);
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        disableRipple
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: controlHeight,
            boxSizing: 'border-box',
            px: '0.625rem',
            borderRadius: '100vh',
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
            color: '#6b7280',
            fontSize,
            fontWeight: 300,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {leadingIcon}
        {selected?.label ?? ''}
        <ChevronDown
          size={12}
          style={{ transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : undefined }}
        />
      </ButtonBase>
      <RoundedMenu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        minWidth={menuMinWidth}
        paperSx={{ mt: '0.5rem' }}
      >
        {options.map(option => (
          <RoundedMenuItem
            key={String(option.value)}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            {option.menuLabel ?? option.label}
          </RoundedMenuItem>
        ))}
      </RoundedMenu>
    </>
  );
}
